import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ChevronDown, Bike } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";

const MaskedLine = ({ children, delay, className }) => (
  <span className={`block overflow-hidden ${className || ""}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="accueil" className="grain relative flex h-screen min-h-[660px] items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src="/images/retrouvailles/hero.jpg" alt="Salle chaleureuse des Retrouvailles de la Douce"
          className="h-full w-full object-cover object-[center_38%] brightness-[1.2]" data-testid="hero-image" />
        <div className="absolute inset-0 bg-[#0D0A08]/40" />
        <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-b from-transparent to-[#FDF6EC]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(13,10,8,0.4)_100%)]" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-5 font-mono2 text-xs uppercase tracking-[0.3em] text-[#D4A359]"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-display text-5xl font-semibold leading-[0.95] tracking-tight text-[#FAF4ED] sm:text-6xl lg:text-8xl" data-testid="hero-title">
          <MaskedLine delay={0.3}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.45} className="italic text-[#E07A2B]">{t.hero.line2}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#B8A898] sm:text-lg"
          data-testid="hero-slogan"
        >
          {t.hero.slogan}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button onClick={() => scrollToId("reservation")} data-testid="hero-cta-reserver"
            className="rounded-full bg-[#E07A2B] px-7 py-3.5 text-sm font-semibold text-[#FDF6EC] transition-all duration-300 hover:bg-[#C25E18] hover:shadow-[0_0_32px_rgba(224,122,43,0.4)]">
            {t.hero.cta1}
          </button>
          <button onClick={() => scrollToId("carte")} data-testid="hero-cta-carte"
            className="rounded-full border border-[#D4A359]/50 px-7 py-3.5 text-sm font-medium text-[#FAF4ED] transition-all duration-300 hover:border-[#E07A2B] hover:bg-[#E07A2B]/10">
            {t.hero.cta2}
          </button>
          <span className="ml-1 inline-flex items-center gap-1.5 text-sm text-[#D4A359]" data-testid="hero-rating-badge">
            <Star size={15} fill="currentColor" /> {t.hero.badge}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-5"
        >
          <a href="https://noldgo.com/restaurants/c6369716-98f1-4462-a9e4-2b9c24324a10" target="_blank" rel="noopener noreferrer" data-testid="hero-partner-noldgo"
            className="inline-flex items-center gap-2 rounded-full border border-[#D4A359]/30 bg-[#0D0A08]/60 px-4 py-2 text-xs text-[#B8A898] backdrop-blur-sm transition-all duration-300 hover:border-[#E07A2B]/60 hover:text-[#E07A2B]">
            <Bike size={14} className="text-[#D4A359]" /> {t.hero.partner}
          </a>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToId("histoire")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-[#8A7969] transition-colors hover:text-[#E07A2B]"
        data-testid="hero-scroll-indicator"
        aria-label={t.hero.scroll}
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ChevronDown size={24} />
        </motion.div>
      </motion.button>
    </section>
  );
};

export default Hero;
